import { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { AdminSession } from '../../api/admin';

// Default Leaflet markers reference relative paths that don't survive the
// Vite build; we side-step the issue entirely by using CircleMarker instead
// of Marker (no image assets required).
void L; // keep import side-effect (defaults Leaflet's CSS-driven sizing)

interface PinGroup {
  lat: number;
  lon: number;
  sessions: AdminSession[];
  cityLabel: string;
  source: 'browser' | 'ip';
}

/** Pull the most accurate lat/lon for a session — browser geo wins over IP. */
function effectiveLatLon(s: AdminSession): { lat: number; lon: number; source: 'browser' | 'ip' } | null {
  if (s.browser_lat != null && s.browser_lng != null) {
    return { lat: s.browser_lat, lon: s.browser_lng, source: 'browser' };
  }
  if (s.geo_lat != null && s.geo_lon != null) {
    return { lat: s.geo_lat, lon: s.geo_lon, source: 'ip' };
  }
  return null;
}

function formatAccuracy(m: number): string {
  if (m < 1000) return `±${Math.round(m)} m`;
  return `±${(m / 1000).toFixed(1)} km`;
}

export function SessionsMap({ sessions }: { sessions: AdminSession[] }) {
  // Group sessions by effective lat/lon. Browser-geo sessions are rounded
  // at 3dp (~100m bucket) so two desks in the same office stay distinct;
  // IP-geo sessions use 2dp (~1km bucket) since the underlying signal is
  // city-level anyway.
  const groups = useMemo<PinGroup[]>(() => {
    const byKey = new Map<string, PinGroup>();
    for (const s of sessions) {
      const loc = effectiveLatLon(s);
      if (!loc) continue;
      const dp = loc.source === 'browser' ? 3 : 2;
      const key = `${loc.source}:${loc.lat.toFixed(dp)}:${loc.lon.toFixed(dp)}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.sessions.push(s);
      } else {
        const cityLabel = [s.geo_city, s.geo_region, s.geo_country].filter(Boolean).join(', ');
        byKey.set(key, {
          lat: loc.lat,
          lon: loc.lon,
          sessions: [s],
          cityLabel: cityLabel || 'Unknown location',
          source: loc.source,
        });
      }
    }
    return Array.from(byKey.values());
  }, [sessions]);

  const noLocation = sessions.length - groups.reduce((n, g) => n + g.sessions.length, 0);
  const browserGeoCount = sessions.filter((s) => s.browser_lat != null && s.browser_lng != null).length;

  const center: [number, number] = groups.length > 0 ? [groups[0]!.lat, groups[0]!.lon] : [20, 0];
  const zoom = groups.length > 0 ? 3 : 2;

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded border border-cockpit-border" style={{ height: 480 }}>
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%', background: '#0b1220' }}
          worldCopyJump
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {groups.map((g) => {
            const activeCount = g.sessions.filter((s) => s.is_active).length;
            const riskyCount = g.sessions.filter(
              (s) => s.connection_risk === 'high' || s.connection_risk === 'critical',
            ).length;
            const radius = Math.min(20, 6 + Math.log2(g.sessions.length + 1) * 4);
            // Red wins over green: a cluster with any risky session is flagged
            // for review even if other sessions in it are clean.
            const color =
              riskyCount > 0 ? '#ef4444' : activeCount > 0 ? '#10b981' : '#94a3b8';
            return (
              <CircleMarker
                key={`${g.source}:${g.lat}:${g.lon}`}
                center={[g.lat, g.lon]}
                radius={radius}
                // Dashed stroke = IP geo (city-level); solid = browser GPS/WiFi (precise).
                // Lets you tell the precise dots from the fuzzy ones at a glance.
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: 0.45,
                  weight: 2,
                  dashArray: g.source === 'ip' ? '4 3' : undefined,
                }}
              >
                <Popup>
                  <div style={{ fontFamily: 'system-ui,sans-serif', fontSize: 12, minWidth: 240 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{g.cityLabel}</div>
                    <div style={{ color: '#64748b', marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span
                        style={{
                          padding: '1px 6px',
                          borderRadius: 10,
                          fontSize: 10,
                          fontFamily: 'monospace',
                          background: g.source === 'browser' ? '#dcfce7' : '#e2e8f0',
                          color: g.source === 'browser' ? '#166534' : '#475569',
                        }}
                      >
                        {g.source === 'browser' ? '📍 GPS / WiFi' : '🌐 IP-geo'}
                      </span>
                      <span>
                        {g.sessions.length} session{g.sessions.length === 1 ? '' : 's'} ·{' '}
                        {activeCount} active
                      </span>
                    </div>
                    {g.sessions.slice(0, 8).map((s) => (
                      <div
                        key={`${s.app}:${s.id}`}
                        style={{
                          borderTop: '1px solid #e2e8f0',
                          padding: '4px 0',
                          fontSize: 11,
                        }}
                      >
                        <span style={{ fontFamily: 'monospace' }}>{s.email}</span>{' '}
                        <span style={{ color: '#64748b' }}>· {s.app}</span>
                        <br />
                        <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{s.ip}</span>
                        <span style={{ marginLeft: 6, color: s.is_active ? '#10b981' : '#94a3b8' }}>
                          {s.is_active ? '· active' : s.logout_at ? '· logged out' : '· expired'}
                        </span>
                        {s.browser_accuracy_m != null && s.geo_source === 'browser' && (
                          <span style={{ marginLeft: 6, color: '#16a34a', fontFamily: 'monospace' }}>
                            · {formatAccuracy(s.browser_accuracy_m)}
                          </span>
                        )}
                      </div>
                    ))}
                    {g.sessions.length > 8 && (
                      <div style={{ color: '#64748b', fontSize: 10, marginTop: 4 }}>
                        + {g.sessions.length - 8} more — filter the table to see all
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      <p className="text-xs text-cockpit-dim">
        Markers prefer <strong>browser geolocation</strong> (GPS / WiFi triangulation, ~10–500 m,
        solid stroke) when the user granted permission, falling back to <strong>IP geo</strong>{' '}
        via ip-api.com (~5–50 km, dashed stroke) when they didn&apos;t. Circle size scales with
        sessions at the same location.{' '}
        <span className="text-cockpit-ok">Green</span> = active,{' '}
        <span className="text-cockpit-dim">grey</span> = logged out / expired,{' '}
        <span className="text-cockpit-crit">red</span> = hosting / VPN / Tor IP (review).{' '}
        <strong>{browserGeoCount}</strong> of <strong>{sessions.length}</strong> session
        {sessions.length === 1 ? '' : 's'} have precise browser geo.
        {noLocation > 0 && (
          <>
            {' '}
            {noLocation} session{noLocation === 1 ? '' : 's'} without a geolocatable IP
            (loopback or private network) {noLocation === 1 ? 'is' : 'are'} not shown on the
            map but {noLocation === 1 ? 'is' : 'are'} listed in the table view.
          </>
        )}
      </p>
    </div>
  );
}
